import { useEffect, useMemo, useState } from "react";

import { BLC_STATUS } from "react-lib/apps/HISV3/common/CommonInterface";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ButtonV4, FieldV4 } from "../common/ui";

import {
  ACTIONS,
  BTN_ACTS,
  SEQUENCE_NAME,
  type DepositData,
  type DepositReceipt,
  type PickedState,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/BillingWalletV4";
import {
  depositFlashOf,
  historyRowsOf,
  refundFlashOf,
  refundNoteOf,
  TX_FILTER_OPTIONS,
  TX_KIND_LABELS,
  WALLET_STATUS_LABELS,
  walletCardsOf,
  type HistoryRow,
  type RefundForm,
  type TxFilter,
  type WalletCard,
} from "./billingWalletModelV4";
import ModalBillingDepositReceiptV4 from "./ModalBillingDepositReceiptV4";
import ModalBillingDepositV4, { type DepositView } from "./ModalBillingDepositV4";
import ModalBillingRefundV4 from "./ModalBillingRefundV4";

// Types
type BillingWalletTabV4Props = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  BillingWalletV4Sequence?: State["BillingWalletV4Sequence"];
  patientId?: number;
  patient?: { hn?: string | null; full_name?: string } | null;
  // callback
  runSequence: RunSequence;
  setProp?: SetProp;
};

type WalletView = "overview" | "history";

// Consts
const ERROR_KEYS = [BTN_ACTS.LOAD, BTN_ACTS.DEPOSIT_OPEN];

const VIEWS: { key: WalletView; label: string }[] = [
  { key: "overview", label: "ภาพรวม" },
  { key: "history", label: "ประวัติรายการ" },
];

/** แท็บ Wallet: ยอดเงินมัดจำต่อการฝาก ประวัติรายการ ฝากเงิน และยกเลิก / คืนเงิน */
const BillingWalletTabV4 = (props: BillingWalletTabV4Props) => {
  const [view, setView] = useState<WalletView>("overview");
  const [groupId, setGroupId] = useState<number | null>(null);
  const [filter, setFilter] = useState<TxFilter>("all");
  const [flash, setFlash] = useState("");
  const [receipt, setReceipt] = useState<{ receipt: DepositReceipt; view: DepositView } | null>(
    null,
  );
  const [refundCard, setRefundCard] = useState<WalletCard | null>(null);

  const seq = props.BillingWalletV4Sequence;

  const loading =
    seq?.patientId !== props.patientId ||
    props.buttonLoadCheck?.[BTN_ACTS.LOAD] === BLC_STATUS.LOADING;

  // Memo
  const cards = useMemo(
    () => walletCardsOf(seq?.groups ?? [], seq?.transactions ?? [], seq?.types ?? []),
    [seq?.groups, seq?.transactions, seq?.types],
  );

  const rows = useMemo(
    () => historyRowsOf(seq?.transactions ?? [], cards, groupId, filter),
    [seq?.transactions, cards, groupId, filter],
  );

  const picked = cards.find((card) => card.group.id === groupId) ?? null;

  // Effect
  useEffect(() => {
    if (!props.patientId) {
      return;
    }

    setView("overview");
    setGroupId(null);
    setFilter("all");
    setFlash("");
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.LOAD,
      patientId: props.patientId,
    });
  }, [props.patientId]);

  // Handler
  const handleChangeView = (key: WalletView) => {
    setView(key);
    setFlash("");
  };

  const handleChangeGroup = (value: string) => {
    setGroupId(value ? Number(value) : null);
  };

  const handleChangeTxFilter = (value: TxFilter) => {
    setFilter(value);
  };

  const handleOpenHistory = (card: WalletCard) => {
    setGroupId(card.group.id ?? null);
    setFilter("all");
    setView("history");
    setFlash("");
  };

  const handleOpenDeposit = () => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.DEPOSIT_OPEN });
  };

  const handleCloseDeposit = () => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.DEPOSIT_CLOSE });
  };

  const handleDeposit = (data: DepositData, deposit: DepositView) => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.DEPOSIT_SUBMIT,
      data,
      onDone: (res) => {
        setReceipt({ receipt: res, view: deposit });
        setView("overview");
        setFlash(depositFlashOf(deposit.name, deposit.amount, deposit.rows, res.code));
      },
    });
  };

  const handlePrintReceipt = () => {
    if (!receipt) {
      return;
    }

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.RECEIPT_PRINT,
      receipt: receipt.receipt,
    });
  };

  const handleCloseReceipt = () => {
    setReceipt(null);
  };

  const handleOpenRefund = (card: WalletCard) => {
    props.setProp?.(`errorMessage.${BTN_ACTS.REFUND}`, null);
    setRefundCard(card);
  };

  const handleCloseRefund = () => {
    setRefundCard(null);
  };

  const handleRefund = (form: RefundForm) => {
    if (!refundCard) {
      return;
    }

    const card = refundCard;

    props.runSequence({
      sequence: SEQUENCE_NAME,
      action: ACTIONS.REFUND,
      group: card.group,
      data: { username: form.username, password: form.password, note: refundNoteOf(form) },
      onDone: () => {
        setRefundCard(null);
        setFlash(refundFlashOf(card, form));
      },
    });
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  // Render
  const renderCard = (card: WalletCard) => {
    const off = card.balance <= 0;

    return (
      <div
        key={card.group.id}
        className={`hv4-bill-wallet-card hv4-bill-wallet-item${off ? " hv4-bill-wallet-item--empty" : ""}`}
      >
        <div className="hv4-bill-wallet-item-head">
          <b>{card.name}</b>
          {card.status && (
            <span className={`hv4-bill-wallet-chip hv4-bill-wallet-chip--${card.status}`}>
              {WALLET_STATUS_LABELS[card.status]}
            </span>
          )}
        </div>
        <div className={`hv4-bill-wallet-balance${off ? " hv4-bill-wallet-balance--off" : ""}`}>
          {formatComma(card.balance)} ฿
        </div>
        <div className="hv4-bill-wallet-meta">
          ได้รับ {formatComma(card.paid)} ฿ (จ่ายจริง {formatComma(card.paid)}) · ใช้ไปแล้ว{" "}
          {formatComma(card.used)} ฿
          {card.refunded > 0 && ` · คืนเงินแล้ว ${formatComma(card.refunded)} ฿`}
        </div>
        <div className="hv4-bill-wallet-meta">เลขที่ใบรับมัดจำ {card.group.receipt_no || "—"}</div>
        <div className="hv4-bill-wallet-actions">
          <ButtonV4 className="hv4-bill-wallet-act" onClick={() => handleOpenHistory(card)}>
            ดูประวัติ Wallet นี้
          </ButtonV4>
          {card.balance > 0 && card.status !== "closed" && (
            <ButtonV4
              className="hv4-bill-wallet-act hv4-bill-wallet-act--refund"
              onClick={() => handleOpenRefund(card)}
            >
              ยกเลิก / คืนเงินมัดจำ
            </ButtonV4>
          )}
        </div>
      </div>
    );
  };

  const renderRow = (row: HistoryRow) => {
    const value = Number(row.tx.value) || 0;

    return (
      <div key={row.tx.id} className="hv4-bill-wallet-row">
        <span>{formatISOtoBEDateTime(row.tx.created).dateTimeShort || "—"}</span>
        <span>
          {row.name}
          {row.kind === "refund" && !!row.tx.remark && (
            <div className="hv4-bill-wallet-note">{row.tx.remark}</div>
          )}
        </span>
        <span>
          <span className={`hv4-bill-wallet-chip hv4-bill-wallet-chip--${row.kind || "empty"}`}>
            {row.kind ? TX_KIND_LABELS[row.kind] : row.tx.type_name || "-"}
          </span>
        </span>
        <span
          className={`hv4-bill-wallet-num ${value < 0 ? "hv4-bill-wallet-minus" : "hv4-bill-wallet-plus"}`}
        >
          {value < 0 ? "−" : "+"}
          {formatComma(Math.abs(value))}
        </span>
        <span className="hv4-bill-wallet-num">{formatComma(row.tx.snapshot_balance || "0")}</span>
        <span>{row.ref}</span>
        <span>{row.staff}</span>
      </div>
    );
  };

  const renderOverview = () => (
    <section className="hv4-bill-wallet">
      <div className="hv4-bill-wallet-card hv4-bill-wallet-total">
        <span>ยอดรวมทุก Wallet</span>
        <b>{formatComma(seq?.total || "0")} ฿</b>
      </div>
      {cards.length > 0 ? (
        <div className="hv4-bill-wallet-grid">{cards.map(renderCard)}</div>
      ) : (
        <div className="hv4-bill-wallet-card hv4-bill-wallet-none">
          {loading
            ? "กำลังโหลด"
            : 'ยังไม่มี Wallet · กด "ฝากเงิน" หรือซื้อแพ็กเกจวงเงินที่เมนู Package'}
        </div>
      )}
    </section>
  );

  const renderHistory = () => (
    <section className="hv4-bill-wallet">
      <div className="hv4-bill-wallet-filters">
        <FieldV4 label="Wallet" htmlFor="hv4-wallet-group">
          <select
            id="hv4-wallet-group"
            className="hv4-input"
            value={groupId ?? ""}
            onChange={(e) => handleChangeGroup(e.target.value)}
          >
            <option value="">ทุก Wallet</option>
            {cards.map((card) => (
              <option key={card.group.id} value={card.group.id}>
                {card.name} · คงเหลือ {formatComma(card.balance)} ฿
              </option>
            ))}
          </select>
        </FieldV4>
        <FieldV4 label="รายการ" htmlFor="hv4-wallet-kind">
          <select
            id="hv4-wallet-kind"
            className="hv4-input"
            value={filter}
            onChange={(e) => handleChangeTxFilter(e.target.value as TxFilter)}
          >
            {TX_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FieldV4>
        {!!picked && picked.balance > 0 && picked.status !== "closed" && (
          <ButtonV4
            className="hv4-bill-wallet-act hv4-bill-wallet-act--refund"
            onClick={() => handleOpenRefund(picked)}
          >
            ยกเลิก / คืนเงินมัดจำ
          </ButtonV4>
        )}
      </div>

      {!!picked && (
        <div className="hv4-bill-wallet-sum">
          <b>{picked.name}</b>
          <span>
            ได้รับ <b>{formatComma(picked.paid)} ฿</b>
          </span>
          <span>
            ใช้ไปแล้ว <b className="hv4-bill-wallet-sum-used">{formatComma(picked.used)} ฿</b>
          </span>
          <span>
            คืนเงินแล้ว{" "}
            <b className="hv4-bill-wallet-sum-refund">{formatComma(picked.refunded)} ฿</b>
          </span>
          <span>
            คงเหลือ <b className="hv4-bill-wallet-sum-left">{formatComma(picked.balance)} ฿</b>
          </span>
        </div>
      )}

      <section
        aria-label="ประวัติรายการ Wallet"
        className="hv4-bill-wallet-card hv4-bill-wallet-table"
      >
        <div className="hv4-bill-wallet-scroll">
          <div className="hv4-bill-wallet-head">
            <span>วันที่เวลา</span>
            <span>Wallet</span>
            <span>รายการ</span>
            <span className="hv4-bill-wallet-num">จำนวน</span>
            <span className="hv4-bill-wallet-num">คงเหลือ</span>
            <span>อ้างอิง</span>
            <span>เจ้าหน้าที่</span>
          </div>
          {rows.map(renderRow)}
          {rows.length === 0 && (
            <div className="hv4-bill-wallet-empty">
              {loading ? "กำลังโหลด" : "ไม่มีรายการตามตัวกรอง"}
            </div>
          )}
        </div>
      </section>
    </section>
  );

  return (
    <div className="hv4-bill-wallet">
      <div className="hv4-bill-wallet-top">
        <h1>Wallet</h1>
        <ButtonV4
          variant="primary"
          disabled={props.buttonLoadCheck?.[BTN_ACTS.DEPOSIT_OPEN] === BLC_STATUS.LOADING}
          onClick={handleOpenDeposit}
        >
          + ฝากเงิน
        </ButtonV4>
      </div>

      <div role="tablist" aria-label="Wallet" className="hv4-bill-wallet-tabs">
        {VIEWS.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={view === item.key}
            className={`hv4-bill-wallet-tab${view === item.key ? " hv4-bill-wallet-tab--on" : ""}`}
            onClick={() => handleChangeView(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {!!flash && (
        <div role="status" className="hv4-bill-wallet-flash">
          {flash}
        </div>
      )}

      {ERROR_KEYS.map((key) => (
        <AlertV4
          key={key}
          tone="danger"
          error={props.errorMessage?.[key]}
          onClose={() => handleCloseError(key)}
        />
      ))}

      {view === "overview" ? renderOverview() : renderHistory()}

      {!!seq?.deposit && (
        <ModalBillingDepositV4
          deposit={seq.deposit}
          types={seq.types ?? []}
          patient={props.patient}
          error={props.errorMessage?.[BTN_ACTS.DEPOSIT_SUBMIT]}
          busy={props.buttonLoadCheck?.[BTN_ACTS.DEPOSIT_SUBMIT] === BLC_STATUS.LOADING}
          onSubmit={handleDeposit}
          onCloseError={() => handleCloseError(BTN_ACTS.DEPOSIT_SUBMIT)}
          onClose={handleCloseDeposit}
        />
      )}

      {!!receipt && (
        <ModalBillingDepositReceiptV4
          receipt={receipt.receipt}
          view={receipt.view}
          hn={props.patient?.hn}
          busy={props.buttonLoadCheck?.[BTN_ACTS.RECEIPT_PRINT] === BLC_STATUS.LOADING}
          onPrint={handlePrintReceipt}
          onClose={handleCloseReceipt}
        />
      )}

      {!!refundCard && (
        <ModalBillingRefundV4
          card={refundCard}
          patient={props.patient}
          busy={props.buttonLoadCheck?.[BTN_ACTS.REFUND] === BLC_STATUS.LOADING}
          error={props.errorMessage?.[BTN_ACTS.REFUND]}
          onConfirm={handleRefund}
          onCloseError={() => handleCloseError(BTN_ACTS.REFUND)}
          onClose={handleCloseRefund}
        />
      )}
    </div>
  );
};

BillingWalletTabV4.displayName = "BillingWalletTabV4";

export default BillingWalletTabV4;
